/**
 * Copy and recovery for every Azure discovery error code, and the partial-list
 * banner text (Option C, C4a). Pure, so the picker body only renders.
 *
 * Recovery kinds: `sign-in` (a sign-in fixes it: expired session, MFA, consent,
 * cancelled), `sign-in-different` (the saved account cannot be verified),
 * `refresh` (the identity moved), `retry` (transient), `none` (informational).
 * The message shown is ours; the raw Azure text is kept as a detail line so a
 * DBA can still read the AADSTS code.
 */
import type {
  AzureDiscoveryCompleteness,
  AzureDiscoveryError,
  AzureDiscoveryErrorCode,
} from '../../../types/azureDiscovery';

export type AzureErrorRecovery = 'sign-in' | 'sign-in-different' | 'refresh' | 'retry' | 'none';

export interface AzureErrorPresentation {
  title: string;
  recovery: AzureErrorRecovery;
  /** The button label for the recovery action. */
  action?: string;
  /** Whether the raw Azure message adds anything (it is hidden for our own refusals). */
  showDetail: boolean;
}

export const PERMISSION_COPY =
  'Listing needs read access to the resource, its resource group or its subscription. Resources reachable only through data roles do not appear — add them by URL or connection string.';

const PRESENTATIONS: Record<AzureDiscoveryErrorCode, AzureErrorPresentation> = {
  'no-identity': { title: 'No Azure identity is signed in.', recovery: 'sign-in', action: 'Sign in with Microsoft', showDetail: false },
  'auth-expired': { title: 'Your Microsoft session expired — sign in again.', recovery: 'sign-in', action: 'Sign in again', showDetail: true },
  'mfa-required': {
    title: 'Azure requires multi-factor authentication for this account. Sign in again to complete it.',
    recovery: 'sign-in',
    action: 'Sign in with MFA',
    showDetail: true,
  },
  'consent-required': {
    title: 'Jam SQL Studio has not been granted access to read your Azure resources yet. Sign in again to grant it (an administrator may have to approve).',
    recovery: 'sign-in',
    action: 'Sign in and grant access',
    showDetail: true,
  },
  'conditional-access': {
    title: 'A Conditional Access policy blocked this sign-in (a compliant or managed device, a location or an app may be required). Signing in again will not help until the policy is satisfied.',
    recovery: 'none',
    showDetail: true,
  },
  'sign-in-cancelled': { title: 'The sign-in was cancelled.', recovery: 'sign-in', action: 'Sign in again', showDetail: false },
  'identity-changed': { title: 'Your Azure account changed. Refresh to list its resources.', recovery: 'refresh', action: 'Refresh', showDetail: false },
  'identity-unverifiable': {
    title: 'The saved Microsoft account cannot be verified against its tokens, so its resources are not listed.',
    recovery: 'sign-in-different',
    action: 'Sign in with a different account…',
    showDetail: false,
  },
  'no-subscriptions': { title: `This identity sees no Azure subscription. ${PERMISSION_COPY}`, recovery: 'retry', action: 'Retry', showDetail: false },
  forbidden: { title: `Azure refused to list resources for this identity. ${PERMISSION_COPY}`, recovery: 'retry', action: 'Retry', showDetail: true },
  throttled: { title: 'Azure throttled the request. Wait a moment, then retry.', recovery: 'retry', action: 'Retry', showDetail: true },
  timeout: { title: 'Azure did not answer within the time limit.', recovery: 'retry', action: 'Retry', showDetail: true },
  cancelled: { title: 'The listing was cancelled.', recovery: 'retry', action: 'Retry', showDetail: false },
  'az-not-found': { title: 'The Azure CLI (az) was not found on this machine.', recovery: 'sign-in', action: 'Sign in with Microsoft', showDetail: true },
  network: { title: 'Azure could not be reached. Check the network connection.', recovery: 'retry', action: 'Retry', showDetail: true },
  unknown: { title: 'Azure Resource Graph rejected the request.', recovery: 'retry', action: 'Retry', showDetail: true },
};

export function presentAzureError(error: AzureDiscoveryError): AzureErrorPresentation {
  return PRESENTATIONS[error.code] ?? PRESENTATIONS.unknown;
}

const REASON_TEXT: Record<NonNullable<AzureDiscoveryCompleteness['reason']>, string> = {
  page_cap: 'Azure returned more resources than the picker lists',
  deadline: 'listing took longer than the time limit',
  throttled: 'Azure throttled the request',
  cancelled: 'the listing was cancelled',
  batch_failed: 'part of the request failed',
};

/** R1 copy: only recovery actions that exist in R1 (Retry; add by URL or connection string). C4b adds "or list fewer subscriptions". */
export function partialListText(completeness: AzureDiscoveryCompleteness): string | undefined {
  if (completeness.complete) return undefined;
  const shown = completeness.shown.toLocaleString('en-US');
  const reason = completeness.reason ? REASON_TEXT[completeness.reason] : 'part of the request failed';
  return `Showing ${shown} resource${completeness.shown === 1 ? '' : 's'}. The list is incomplete because ${reason}. Retry, or add a missing resource by URL or connection string.`;
}
